import { useCallback, useEffect, useState, FormEvent } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { useApp } from '../contexts/AppContext';
import type { Session } from '../types/app';
import { BACKEND_URL } from '../config';

interface AccountSecurityProps {
  session: Session;
  onSessionUpdate: (session: Session) => void;
}

interface LinkedProvider {
  provider: 'google' | 'facebook' | 'apple';
  email: string | null;
  display_name: string | null;
  last_login_at: string | null;
}

interface LinkedPseudo {
  pseudo: string;
  pseudo_display: string;
  has_photo: boolean;
}

interface AccountStatus {
  pseudo: string;
  pseudo_display: string;
  email: string | null;
  has_password: boolean;
  method: string | null;
  linked_providers: LinkedProvider[];
  linked_pseudos: LinkedPseudo[];
  linked_pseudos_count: number;
}

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

const PROVIDER_REVOKE_URLS: Record<string, string> = {
  google: 'https://myaccount.google.com/permissions',
  facebook: 'https://www.facebook.com/settings?tab=applications',
};

/**
 * Affichage des fournisseurs non liés.
 *
 * Désactivé pour l'instant : le rattachement d'un nouveau fournisseur passe
 * par une déconnexion/reconnexion via la page de login (où il est déjà
 * détecté que l'email correspond). Ré-activer ce flag rendra visible les
 * boutons « Lier » avec redirection vers le flux OAuth.
 */
const SHOW_UNLINKED_PROVIDERS = false;

function authHeaders(token?: string): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Décrit les fournisseurs liés sous une forme lisible :
 *   []                  -> null
 *   ['google']          -> 'Google'
 *   ['google','facebook'] -> 'Google ou Facebook'
 */
function describeLinkedProviders(providers: LinkedProvider[]): string | null {
  const names = providers
    .map((p) => PROVIDER_LABELS[p.provider] || p.provider)
    .filter(Boolean);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} ou ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} ou ${names[names.length - 1]}`;
}

export default function AccountSecurity({ session, onSessionUpdate }: AccountSecurityProps) {
  const { bumpAvatarVersion } = useApp();
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/account-status`, {
        headers: authHeaders(session.token),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Impossible de charger le statut du compte');
      setStatus(data as AccountStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [session.token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (loading && !status) {
    return (
      <section className="settings-section">
        <p className="settings-toggle-description">Chargement…</p>
      </section>
    );
  }

  if (error && !status) {
    return (
      <section className="settings-section">
        <div className="login-error">{error}</div>
      </section>
    );
  }

  if (!status) return null;

  return (
    <section className="settings-section account-security">
      {info && <div className="success-message" style={{ marginBottom: 16 }}>{info}</div>}
      {error && <div className="login-error" style={{ marginBottom: 16 }}>{error}</div>}

      <EmailBlock
        email={status.email}
        token={session.token}
        onUpdated={(msg) => {
          setInfo(msg);
          setError('');
          loadStatus();
        }}
        onError={(msg) => {
          setError(msg);
          setInfo('');
        }}
      />

      <PasswordBlock
        status={status}
        token={session.token}
        onSuccess={(msg) => {
          setInfo(msg);
          setError('');
          loadStatus();
        }}
        onError={(msg) => {
          setError(msg);
          setInfo('');
        }}
      />

      <ProvidersBlock
        status={status}
        token={session.token}
        onChange={(msg) => {
          setInfo(msg);
          setError('');
          loadStatus();
        }}
        onError={(msg) => {
          setError(msg);
          setInfo('');
        }}
      />

      <PseudoSwitcherBlock
        status={status}
        session={session}
        onSwitched={(updated) => {
          onSessionUpdate(updated);
          bumpAvatarVersion();
          loadStatus();
        }}
        onError={(msg) => {
          setError(msg);
          setInfo('');
        }}
      />
    </section>
  );
}

/* ============================================================ */
/* Email de récupération                                        */
/* ============================================================ */

function EmailBlock({
  email,
  token,
  onUpdated,
  onError,
}: {
  email: string | null;
  token?: string;
  onUpdated: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(email || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setValue(email || '');
  }, [email, open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      onError('Veuillez saisir une adresse email.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/update-recovery-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_email: 'Adresse email invalide.',
          email_required: 'Veuillez saisir une adresse email.',
          update_failed: 'Impossible de mettre à jour l\'email.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur');
      }
      setOpen(false);
      onUpdated(email ? 'Email de récupération mis à jour.' : 'Email de récupération défini.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="account-block">
      <div className="account-block-header">
        <h3>Email de récupération</h3>
        <button
          type="button"
          className="account-action-btn"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Annuler' : email ? 'Modifier' : 'Définir'}
        </button>
      </div>

      {!open && (
        <>
          {email ? (
            <p className="account-block-value">{email}</p>
          ) : (
            <p className="settings-toggle-description">
              <strong>Aucune adresse email de récupération.</strong>{' '}
              Définissez-en une pour pouvoir utiliser « Mot de passe oublié »
              ou le lien magique de connexion.
            </p>
          )}
          <p className="settings-toggle-description" style={{ marginTop: 6 }}>
            Cette adresse sert à recevoir les liens de connexion / réinitialisation.
            Elle est distincte de votre <em>email de contact public</em> (visible
            depuis la section <strong>Profil</strong>).
          </p>
        </>
      )}

      {open && (
        <form onSubmit={submit} className="account-form">
          <div className="form-group">
            <label>Adresse email</label>
            <input
              type="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="votre@email.com"
              autoComplete="email"
              required
            />
          </div>
          <p className="settings-toggle-description" style={{ marginTop: -4 }}>
            La modification est immédiate, sans vérification par email.
            En cas d'erreur de saisie, vous pourrez vous reconnecter avec
            votre pseudo et mot de passe (ou via votre fournisseur
            Google/Facebook si vous l'avez lié) pour la corriger.
          </p>
          <button type="submit" className="login-button" disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      )}
    </div>
  );
}

/* ============================================================ */
/* Mot de passe                                                 */
/* ============================================================ */

function PasswordBlock({
  status,
  token,
  onSuccess,
  onError,
}: {
  status: AccountStatus;
  token?: string;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  const needsSet = !status.has_password;
  // Si l'utilisateur s'est connecté via OAuth/lien magique, il peut définir un
  // nouveau mot de passe sans connaître l'ancien (set-password) même s'il en a déjà un.
  const canSetWithoutOld = status.method === 'oauth' || status.method === 'magic_link';
  const providersDesc = describeLinkedProviders(status.linked_providers);
  // Si on connaît la session courante OAuth ET que ce fournisseur fait partie
  // des liés, on l'affiche en priorité (« Google » plutôt que « Google ou Facebook »).
  const currentProviderName =
    status.method === 'oauth' && status.linked_providers.length > 0
      ? PROVIDER_LABELS[status.linked_providers[0].provider] || providersDesc
      : providersDesc;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw.length < 6) {
      onError('Le mot de passe doit faire au moins 6 caractères.');
      return;
    }
    if (newPw !== confirmPw) {
      onError('Les mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      const useSet = needsSet || canSetWithoutOld;
      const endpoint = useSet ? '/api/auth/set-password' : '/api/auth/change-password';
      const body = useSet
        ? { new_password: newPw }
        : { old_password: oldPw, new_password: newPw };

      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_old_password: 'Le mot de passe actuel est incorrect.',
          password_too_short: 'Le mot de passe est trop court (6 min).',
          must_use_change_password: 'Reconnectez-vous via le lien magique ou OAuth pour définir un mot de passe sans saisir l\'ancien.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur');
      }
      setOpen(false);
      setOldPw(''); setNewPw(''); setConfirmPw('');
      onSuccess(needsSet ? 'Mot de passe défini avec succès.' : 'Mot de passe modifié avec succès.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="account-block">
      <div className="account-block-header">
        <h3>Mot de passe</h3>
        <button
          type="button"
          className="account-action-btn"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Annuler' : needsSet ? 'Définir un mot de passe' : 'Modifier'}
        </button>
      </div>

      {!open && (
        <p className="settings-toggle-description">
          {needsSet ? (
            <>
              <strong>Aucun mot de passe défini.</strong> Vous vous connectez actuellement via{' '}
              {status.method === 'oauth'
                ? currentProviderName || 'votre fournisseur'
                : status.method === 'magic_link'
                  ? 'un lien magique reçu par email'
                  : 'un autre moyen'}
              .{' '}
              Définir un mot de passe vous permettra de vous connecter directement
              avec votre pseudo et mot de passe (utile aussi pour le client de jeu classique).
            </>
          ) : canSetWithoutOld ? (
            <>
              Mot de passe défini. Comme vous êtes connecté via{' '}
              {status.method === 'oauth'
                ? currentProviderName || 'votre fournisseur'
                : 'un lien magique'}
              , vous pouvez le changer sans saisir l'ancien.
            </>
          ) : (
            'Mot de passe défini. Pour le modifier, saisissez l\'ancien puis le nouveau.'
          )}
        </p>
      )}

      {open && (
        <form onSubmit={submit} className="account-form">
          {!needsSet && !canSetWithoutOld && (
            <div className="form-group">
              <label>Mot de passe actuel</label>
              <input
                type="password"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          )}
          <div className="form-group">
            <label>Nouveau mot de passe</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="6 caractères minimum"
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label>Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <button type="submit" className="login-button" disabled={busy}>
            {busy ? 'Enregistrement…' : needsSet ? 'Définir' : 'Mettre à jour'}
          </button>
        </form>
      )}
    </div>
  );
}

/* ============================================================ */
/* Comptes OAuth liés                                           */
/* ============================================================ */

function ProvidersBlock({
  status,
  token,
  onChange,
  onError,
}: {
  status: AccountStatus;
  token?: string;
  onChange: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const linkedIds = new Set(status.linked_providers.map((p) => p.provider));
  const knownProviders: Array<'google' | 'facebook'> = ['google', 'facebook'];
  // Liste effectivement affichée : tous les fournisseurs si on veut aussi
  // proposer le bouton « Lier », sinon uniquement ceux déjà liés.
  const visibleProviders = SHOW_UNLINKED_PROVIDERS
    ? knownProviders
    : knownProviders.filter((p) => linkedIds.has(p));
  const otherWayCount =
    (status.has_password ? 1 : 0) +
    (status.email ? 1 : 0) + // lien magique disponible si email présent
    status.linked_providers.length;

  const unlink = async (provider: string) => {
    setBusy(provider);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/auth/oauth/platform-data/${encodeURIComponent(provider)}`,
        { method: 'DELETE', headers: authHeaders(token) },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Détachement impossible');
      onChange(`${PROVIDER_LABELS[provider] || provider} dissocié.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  const confirmUnlink = (provider: 'google' | 'facebook') => {
    const wouldBeLastOAuth = status.linked_providers.length === 1;
    const hasOtherWay = status.has_password || status.email;
    const warning = wouldBeLastOAuth && !hasOtherWay
      ? `⚠️ Ceci est votre dernier moyen de connexion. Vous ne pourrez plus accéder à ce compte.`
      : wouldBeLastOAuth
      ? `Vous ne pourrez plus vous connecter avec ${PROVIDER_LABELS[provider]}. Vous garderez ${status.has_password ? 'votre mot de passe' : 'le lien magique par email'} comme moyen de connexion.`
      : `Vous ne pourrez plus vous connecter avec ${PROVIDER_LABELS[provider]} (mais d'autres moyens restent disponibles).`;

    if (window.confirm(`${warning}\n\nConfirmer la dissociation ?`)) {
      unlink(provider);
    }
  };

  return (
    <div className="account-block">
      <div className="account-block-header">
        <h3>Fournisseurs d'authentification</h3>
      </div>

      {visibleProviders.length === 0 ? (
        <p className="settings-toggle-description">
          Aucun fournisseur (Google, Facebook…) n'est associé à ce pseudo.
          Pour en lier un, déconnectez-vous puis reconnectez-vous via le bouton
          du fournisseur sur la page de connexion : si l'email correspond à
          votre email de récupération, le rattachement se fera automatiquement.
        </p>
      ) : (
        <ul className="account-providers-list">
          {visibleProviders.map((p) => {
            const linked = status.linked_providers.find((lp) => lp.provider === p);
            return (
              <li key={p} className={`account-provider-row${linked ? ' linked' : ''}`}>
                <div className="account-provider-info">
                  <span className="account-provider-dot" aria-hidden="true" />
                  <div>
                    <strong>{PROVIDER_LABELS[p]}</strong>
                    <span className="account-provider-meta">
                      {linked
                        ? linked.email || 'Compte lié (email non communiqué)'
                        : 'Non lié'}
                    </span>
                  </div>
                </div>
                {linked ? (
                  <button
                    type="button"
                    className="account-action-btn account-action-btn-danger"
                    onClick={() => confirmUnlink(p)}
                    disabled={busy === p}
                  >
                    {busy === p ? '…' : 'Dissocier'}
                  </button>
                ) : (
                  <a
                    className="account-action-btn account-action-btn-muted"
                    href={`${BACKEND_URL}/api/auth/oauth/start/${p}`}
                    title={`Reconnectez-vous via ${PROVIDER_LABELS[p]} pour lier ce fournisseur à votre pseudo`}
                  >
                    Lier
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {visibleProviders.length > 0 && (
        <p className="settings-toggle-description" style={{ marginTop: 10 }}>
          Pour lier un autre fournisseur, déconnectez-vous puis utilisez le
          bouton correspondant sur la page de connexion. Tant que l'email
          côté fournisseur correspond à votre email de récupération, votre
          pseudo sera retrouvé automatiquement.
        </p>
      )}
      {linkedIds.size > 0 && (
        <p className="settings-toggle-description" style={{ marginTop: 4 }}>
          Pour <strong>révoquer totalement</strong> l'autorisation côté
          fournisseur (depuis ses propres réglages) :{' '}
          {[...linkedIds].map((p, i) => PROVIDER_REVOKE_URLS[p] ? (
            <span key={p}>
              {i > 0 && ', '}
              <a href={PROVIDER_REVOKE_URLS[p]} target="_blank" rel="noopener noreferrer">
                {PROVIDER_LABELS[p]}
              </a>
            </span>
          ) : null)}.
        </p>
      )}
      {otherWayCount <= 1 && (
        <p className="settings-toggle-description" style={{ marginTop: 4, color: 'var(--color-warning, #d97706)' }}>
          ⚠️ Pensez à <strong>définir un mot de passe</strong> ou à renseigner
          un email de récupération pour ne pas dépendre d'un seul moyen de connexion.
        </p>
      )}
    </div>
  );
}

/* ============================================================ */
/* Switcher de pseudo                                           */
/* ============================================================ */

function PseudoSwitcherBlock({
  status,
  session,
  onSwitched,
  onError,
}: {
  status: AccountStatus;
  session: Session;
  onSwitched: (newSession: Session) => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  if (status.linked_pseudos_count === 0) return null;

  const switchTo = async (pseudo: string) => {
    setBusy(pseudo);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/switch-pseudo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(session.token) },
        body: JSON.stringify({ pseudo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Bascule impossible');
      onSwitched({
        ...session,
        pseudo: data.user.pseudo,
        pseudoDisplay: data.user.pseudo_display || data.user.pseudo,
        token: data.token,
        hashPass: data.hash_pass,
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="account-block">
      <div className="account-block-header">
        <h3>Mes autres pseudos ({status.linked_pseudos_count + 1})</h3>
      </div>
      <p className="settings-toggle-description">
        Pseudos partageant la même adresse email. Basculez sans vous déconnecter.
      </p>
      <ul className="account-pseudo-list">
        <li className="account-pseudo-row account-pseudo-row-current">
          <PlayerAvatar pseudo={status.pseudo} size={42} />
          <div className="account-pseudo-info">
            <strong>{status.pseudo_display}</strong>
            <span className="account-pseudo-badge">Actuel</span>
          </div>
        </li>
        {status.linked_pseudos.map((p) => (
          <li key={p.pseudo} className="account-pseudo-row">
            <PlayerAvatar pseudo={p.pseudo} size={42} />
            <div className="account-pseudo-info">
              <strong>{p.pseudo_display}</strong>
            </div>
            <button
              type="button"
              className="account-action-btn"
              onClick={() => switchTo(p.pseudo)}
              disabled={Boolean(busy)}
            >
              {busy === p.pseudo ? '…' : 'Basculer'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
